import { usePropertyEnricher } from "./usePropertyEnricher";
import { PropertyViewModel } from "../ViewModels/PropertyViewModel";

import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    useDetailLifeCycle,
    useExportAction,
    UIAction,
    AppEnumHelper,
} from "@/Shared";

import { useDocument, useProperty } from "@/OneLiners";
import { useDeleteMedia } from "@/Domains/MediaFile";
import { PropertyModel } from "../Models";


/**
 * usePropertyDetail hook
 *
 * Domain-specific detail hook for Property entities.
 * Orchestrates domain data enrichment, document handling,
 * deletion workflow, UI feedback, and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the PropertyViewModel when data is available.
 * - Enrich the property with associated documents.
 * - Handle PDF export and generation for the current property.
 * - Handle deletion of media with access control.
 * - Expose entity VM, UI actions, and access rights to the component layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<PropertyViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<PropertyViewModel>
 */
export function usePropertyDetail(props: DomainHookProps): DomainDetailHook<PropertyViewModel> {
    const { extraArgs, permissionHook, accessRight, uiFeedbackHooks } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Property;

    /**
     * Property-related enums exposed to the UI layer
     */
    const { PropertyEnums } = useAppEnum();
    const {DocumentEnums} = useAppEnum();

    /**
     * Property domain CRUD operations and state
     */
    const propertyHook = useProperty();

    /**
     * Document data state
     */
    const { dataMap: documentMap, stateResponse: documentResponse } = useDocument();

    /**
     * Property domain enrichment facade
     */
    const {
        getStrictEnrichedProperty,
        findByDocumentResource,
        enricherResponse,
    } = usePropertyEnricher();

    /**
    * Combined loading and error state for detail initialization
    */
    const response = {
        status: ActionStatusHelper.combine(
            propertyHook.stateResponse.status,
            documentResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: propertyHook.stateResponse.error ??
            documentResponse.error ??
            enricherResponse.error
    }

    /**
     * Media deletion handler
     */
    const { deletePropertyMedia, deleteResponse } = useDeleteMedia(
        uiFeedbackHooks, () => entityVM &&
            propertyHook.fetchAction.execute?.(entityVM?.id)
    );

    const { isSucceeded: generateIsSucceed } = ActionStatusHelper.is(propertyHook.generateAction.response.status);

    /**
     * Detail lifecycle hook
     * - Initializes the entity VM
     * - Handles data fetching, enrichment, and readiness state
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: propertyHook,
        buildVM: (currentEntity: PropertyModel, isDisplaying: boolean) => {
            const filteredDocumentMap = findByDocumentResource(documentMap, domainType, currentEntity.id);
            return new PropertyViewModel({
                property: getStrictEnrichedProperty(currentEntity),
                documentMap: filteredDocumentMap,
                viewEnums: PropertyEnums,
                isDisplaying,
                accessRight
            })
        },
        deps: [generateIsSucceed, deleteResponse.status]
    });

    const {entityVM, requestId} = lifeCycleHook;


    const docCategory =  AppEnumHelper.getByValue(DocumentEnums.DocumentCategoryEnum, domainType);
    const docTypes = (docCategory?.children ?? []).map(child => child.value as string);

    const {computeUIActions} = useExportAction<PropertyModel, PropertyViewModel>({
        accessRight : props.accessRight,
        uiFeedbackHooks: props.uiFeedbackHooks,
        entityVM : entityVM,
        domainType,
        domainLabel,
        entityHook: propertyHook,
        supportedDocTypes : docTypes.length === 0 ? undefined : docTypes,
        noFormDocTypes : docTypes.length === 0 ? undefined : docTypes,
    });

    const deleteMediaAction = () : UIAction => {
        const canDelete = accessRight.canDelete(domainType);
         return {
            key: `${domainType}-media-delete-${requestId}`,
            label: "Exporter",
            onClick: (id?: number) => (canDelete && id)
                ? deletePropertyMedia(id)
                : undefined,

            placement: "media",
            type: 'execute',
            icon: 'FaTrash',
            disabled: !canDelete,
            loading: ActionStatusHelper.isLoading(deleteResponse.status),
            tooltip: canDelete
                ? "Cliquez pour supprimer ce media."
                : "Vous n'avez pas les droits suffisants pour supprimer."
        };
    }

    /**
     * Public API exposed to the component layer
     */
    return {
        ...lifeCycleHook,
        computeUIActions : () => [
            ...computeUIActions(), 
            deleteMediaAction()
        ],
    }
}